import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabaseApi } from '~/supabase/api'

type UploadFileToBucketOptions = {
  bucketName?: string
  contentType?: string
  folderPath?: string
}

export const useUploadFileToBucket = () => {
  return useMutation({
    mutationFn: async ({
      filePath,
      fileContent,
      options,
    }: {
      filePath: string
      fileContent: string
      options?: UploadFileToBucketOptions
    }) => {
      try {
        // Convert base64 to Uint8Array
        const binaryString = atob(fileContent)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        // Set default values for imports bucket and inventory folder
        const bucketName = options?.bucketName || 'imports'
        const folderPath = options?.folderPath || 'inventory'
        const fullPath = `${folderPath}/${filePath}`

        // Upload file to Supabase storage
        const { error } = await supabaseApi.storage.from(bucketName).upload(fullPath, bytes, {
          contentType: options?.contentType || 'application/octet-stream',
          upsert: true,
        })

        if (error) {
          throw new Error(`Error uploading file: ${error.message}`)
        }
        const { data, error: infoError } = await supabaseApi.storage.from(bucketName).info(fullPath)
        if (infoError) {
          throw new Error(`Error getting info of file: ${infoError.message}`)
        }
        return data.name
      } catch (error) {
        console.error('Error uploading file to Supabase:', error)
        throw error
      }
    },
    onSuccess: () => {
      toast.success('File uploaded successfully')
    },
    onError: (error: Error) => {
      console.error('Error uploading file:', error)
      toast.error(`Failed to upload file: ${error.message}`)
    },
  })
}
